import { Specialty } from './models';

export const SPECIALTY_ORDER: string[] = ["cardiology","orthopaedics","neurology","oncology","gastroenterology","respiratory","urology","womens-health","ophthalmology","mental-health"];

export const SPECIALTIES: Specialty[] = [
  {
    "slug": "cardiology",
    "name": "Cardiology",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M20 8.5c0 5-8 11-8 11s-8-6-8-11a4 4 0 0 1 8-1 4 4 0 0 1 8 1z\"></path></svg>",
    "eyebrow": "Cardiology",
    "h1": "Not Sure About Your Heart Diagnosis or Treatment Plan? Get a Second Opinion",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports, choose a cardiologist from our listed experts, and get clear, independent advice on your condition and your options.",
    "btn1": "Choose a cardiologist",
    "btn2": "How it works",
    "reviewText": "Confirmatory reviews for stenting vs. CABG surgery, complex valve interventions, arrhythmia ablation, and progressive heart failure regimens.",
    "steps": [
      {
        "h": "Pick a cardiologist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload ECG, echo, angiography, blood tests, and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you have chest pain, breathlessness, or fainting right now, go to the nearest emergency room.",
    "types": null,
    "condTitle": "Conditions Cardiologists Look After",
    "conds": [
      "Coronary artery disease and angina",
      "Heart attack and recovery after it",
      "Heart failure and cardiomyopathy",
      "Arrhythmias such as atrial fibrillation",
      "High blood pressure and high cholesterol",
      "Heart valve disease",
      "Congenital heart conditions"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been advised angioplasty, stenting or bypass surgery",
      "You need a valve repair or replacement",
      "You are told to start long-term heart medication or a device such as a pacemaker",
      "Your test results are unclear, or your symptoms continue despite treatment",
      "You want to understand your diagnosis and the alternatives"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "ECG and echocardiogram reports, stress test or CT angiography results, angiography films or reports, recent blood tests (lipids, HbA1c, kidney function), a list of current medicines, and any discharge summaries.",
    "docsEyebrow": "Cardiology",
    "docsTitle": "Our Cardiologists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-cardiology-1",
      "doctor-cardiology-2",
      "doctor-cardiology-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I had been told I needed bypass surgery and I was scared. The cardiologist went through my angiography report with me and explained which blockages mattered and why. I finally understood my options and felt able to decide.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“The doctor listened without rushing and answered every question, even the small ones. Uploading my reports was simple, and I received a written summary that I could show my own doctor.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“Booking, sharing records and the video call all went smoothly, and the fee was shown upfront. I got the second opinion within two days, without travelling to another city.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor treat me?",
        "a": "No. The cardiologist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get the opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  },
  {
    "slug": "oncology",
    "name": "Oncology",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M6 3v7a6 6 0 0 0 12 0V3\"></path><path d=\"M9 21h6\"></path></svg>",
    "eyebrow": "Oncology",
    "h1": "Facing Cancer Diagnosis or Treatment Decision? Get a Second Opinion with WellSpring",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports, choose an oncologist from our listed experts, and get clear, independent advice on your diagnosis, staging and treatment options, so you can move forward with confidence.",
    "btn1": "Choose an oncologist",
    "btn2": "How it works",
    "reviewText": "Tumour re-staging, precision medicine guidance, radiation vs. surgical pathways, and immunotherapy assessments.",
    "steps": [
      {
        "h": "Pick an oncologist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload biopsy and pathology reports, PET-CT, CT or MRI scans, blood tests, and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you have uncontrolled bleeding, severe breathlessness, high fever during chemotherapy, or sudden severe pain, go to the nearest emergency room immediately. If you are already in treatment, do not stop or change your medicines without speaking to your treating team.",
    "types": {
      "title": "Types of Oncology Specialists",
      "items": [
        {
          "strong": "Medical oncologists",
          "text": "treat cancer with chemotherapy, targeted therapy, immunotherapy and hormonal therapy"
        },
        {
          "strong": "Surgical oncologists",
          "text": "remove tumours and nearby tissue through surgery"
        },
        {
          "strong": "Radiation oncologists",
          "text": "use radiation to treat cancer"
        },
        {
          "strong": "Haemato-oncologists",
          "text": "treat blood cancers such as leukaemia and lymphoma"
        }
      ]
    },
    "condTitle": "Cancers Oncologists Look After",
    "conds": [
      "Breast cancer",
      "Lung cancer",
      "Colorectal and other digestive system cancers",
      "Prostate, kidney and bladder cancers",
      "Cervical, ovarian and uterine cancers",
      "Head and neck cancers",
      "Blood cancers such as leukaemia, lymphoma and myeloma",
      "Skin cancers and sarcomas"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have just been diagnosed and want to confirm the type and stage of your cancer",
      "You have been given a treatment plan and want to know whether other options exist",
      "You have been advised major surgery, chemotherapy, radiation or a combination",
      "Your cancer has come back, or your current treatment does not seem to be working",
      "Your biopsy or scan results are unclear",
      "You are considering newer options such as targeted therapy or immunotherapy",
      "You want to understand your diagnosis, outlook and next steps"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "Biopsy and pathology reports (and slides or blocks if available), PET-CT, CT or MRI scan reports and images, blood tests and tumour marker results, any genetic or molecular test reports, your current treatment plan, a list of medicines, and hospital discharge summaries or surgery notes. A short timeline for your symptoms, tests and treatments so far is also very helpful.",
    "docsEyebrow": "Oncology",
    "docsTitle": "Our Oncologists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-oncology-1",
      "doctor-oncology-2",
      "doctor-oncology-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "Stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“After my diagnosis, everything felt like a blur. The oncologist went through my biopsy and scan reports step by step and explained my cancer type, stage and what each treatment would mean for me. For the first time, I understood what I was dealing with.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“The doctor was patient and kind and never made me feel rushed. He answered the questions my family and I had been afraid to ask, and he was honest about the options without scaring us.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“I didn't have to travel to another city while my mother was unwell. We uploaded her reports from home, had a video call within a few days, and received a clear written summary we could take to her treating doctor.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor take over my treatment?",
        "a": "No. The oncologist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Should I stop my current treatment while I wait for a second opinion?",
        "a": "Do not stop or delay treatment on your own. Speak to your doctor about timing, as some cancers need treatment to begin without delay."
      },
      {
        "q": "Can a family member join the consultation?",
        "a": "Yes. Many patients find it helpful to have a family member or caregiver join the call to listen, ask questions and take notes."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": "Second opinions are for informational support and do not replace care from your treating doctor. Not for medical emergencies."
  },
  {
    "slug": "orthopaedics",
    "name": "Orthopaedics",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M8 3v4M16 3v4M4 9h16\"></path><rect x=\"4\" y=\"5\" width=\"16\" height=\"16\" rx=\"1\"></rect></svg>",
    "eyebrow": "Orthopaedics",
    "h1": "Advised Surgery for Your Joint, Bone or Spine Problem? Get a Second Opinion First",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and scans, choose an orthopaedic specialist from our listed experts, and get clear, independent advice on your condition and your options, including whether surgery is needed at all.",
    "btn1": "Choose an orthopaedic specialist",
    "btn2": "How it works",
    "reviewText": "Independent confirmation of the necessity of joint replacement, disc repair vs. spinal fusion, and evidence-based non-surgical alternatives.",
    "steps": [
      {
        "h": "Pick a specialist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload X-rays, MRI or CT scans, reports, and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you have a severe injury, an open wound over a broken bone, numbness in the legs, or loss of bladder or bowel control, go to the nearest emergency room.",
    "types": null,
    "condTitle": "Conditions Orthopaedic Specialists Look After",
    "conds": [
      "Fractures and bones that are slow to heal",
      "Arthritis of the knee, hip, shoulder and other joints",
      "Ligament and meniscus injuries (such as ACL tears)",
      "Shoulder problems, including rotator cuff tears and frozen shoulder",
      "Back and neck pain, slipped disc and sciatica",
      "Spinal stenosis and spinal deformities",
      "Sports injuries",
      "Osteoporosis and bone weakness",
      "Bone and joint conditions in children"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been advised knee, hip or shoulder replacement",
      "You have been advised spine surgery",
      "You have been advised arthroscopy or ligament reconstruction",
      "Your pain continues despite treatment, or scan findings and symptoms don't seem to match",
      "You want to know whether physiotherapy, injections or other non-surgical options could work",
      "You want to understand your diagnosis and the alternatives"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "X-rays, MRI and CT scan reports (and the images if you have them), recent blood tests, a list of current medicines, previous treatment and physiotherapy notes, operative notes or discharge summaries if you have had surgery, and a short note on when your symptoms started and what makes them better or worse.",
    "docsEyebrow": "Orthopaedics",
    "docsTitle": "Our Orthopaedic Specialists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-orthopaedics-1",
      "doctor-orthopaedics-2",
      "doctor-orthopaedics-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I was told I needed a knee replacement and had only a few minutes to understand why. The specialist went through my X-rays with me, explained how advanced the arthritis really was, and laid out what I could try first. I came away knowing what my options were.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“My MRI report sounded alarming, and I was nervous about spine surgery. The doctor listened to my full history and explained my scan in plain language. He never rushed me and answered every question, even the small ones.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“Uploading my scans was easy, the fee was clear from the start, and I received a written summary within two days. I could show it to my own doctor and discuss the next step with confidence.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor operate on me?",
        "a": "No. The specialist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Do I need to share my actual scan images?",
        "a": "Reports are useful, but the images themselves (X-ray, MRI, CT) let the specialist give a more accurate opinion. Share both if you can."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get the opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  },
  {
    "slug": "neurology",
    "name": "Neurology",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><circle cx=\"12\" cy=\"12\" r=\"8\"></circle><path d=\"M12 8v4l2.5 2.5\"></path></svg>",
    "eyebrow": "Neurology",
    "h1": "Unsure About a Brain, Nerve, or Spine Diagnosis? Get a Second Opinion",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and scans, choose a neurologist from our listed experts, and get clear, independent advice on your condition, your test results and your treatment options.",
    "btn1": "Choose a neurologist",
    "btn2": "How it works",
    "reviewText": "Clarification on ambiguous brain or spine scans, movement disorder therapies, epilepsy protocols, and neuro-oncology.",
    "steps": [
      {
        "h": "Pick a neurologist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload MRI or CT scans, EEG, nerve test reports, blood tests, and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you or someone near you has sudden weakness on one side of the body, facial drooping, slurred speech, a sudden severe headache, a seizure that does not stop, or loss of consciousness, go to the nearest emergency room immediately.",
    "types": null,
    "condTitle": "Conditions Neurologists Look After",
    "conds": [
      "Headache and migraine",
      "Epilepsy and seizures",
      "Stroke and recovery after stroke",
      "Parkinson's disease and other movement disorders",
      "Memory loss and dementia",
      "Multiple sclerosis",
      "Neuropathy (nerve damage) and numbness or tingling",
      "Dizziness and balance problems",
      "Muscle and nerve-muscle disorders",
      "Sleep disorders"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been given a diagnosis such as epilepsy, multiple sclerosis or Parkinson's disease and want it confirmed",
      "Your symptoms continue despite treatment, or you have been on many medicines without clear improvement",
      "Your scan or test results are unclear or seem different from how you feel",
      "You have been advised long-term medication, or have been told surgery or an advanced procedure is needed",
      "You have been told \"everything is normal\" but your symptoms continue",
      "You want to understand your diagnosis, prognosis and alternatives"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "MRI or CT scan reports (and images if you have them), EEG or nerve conduction study reports, blood tests, a list of current and past medicines, hospital discharge summaries, and a short symptom diary noting when episodes occur, how long they last and what triggers them. If possible, include a phone video of any episodes, such as tremors or seizures, and note down what family members observed.",
    "docsEyebrow": "Neurology",
    "docsTitle": "Our Neurologists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-neurology-1",
      "doctor-neurology-2",
      "doctor-neurology-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I had been on seizure medicine for years and was never sure whether I needed all of them. The neurologist reviewed my EEG and history, explained my type of epilepsy in simple terms, and helped me understand what to discuss with my own doctor.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“Living with unexplained numbness and being told my tests were normal was frustrating. This doctor listened to the whole story, asked questions nobody had asked before, and never made me feel rushed.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“I uploaded my father's MRI and reports from home, and we had a video call within two days. The written summary was clear, and our family finally understood what to expect and what to ask at the next appointment.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor take over my treatment?",
        "a": "No. The neurologist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Can I share videos of my symptoms?",
        "a": "Yes. Short phone videos of episodes such as tremors, seizures, or unusual movements can help the specialist understand your condition better. Share them only with the doctor you choose."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  },
  {
    "slug": "gastroenterology",
    "name": "Gastroenterology",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M4 7h16M6 4h12v16H6z\"></path><path d=\"M9 11h6M9 15h4\"></path></svg>",
    "eyebrow": "Gastroenterology",
    "h1": "Unsure About a Digestive or Liver Diagnosis, or a Recommended Procedure? Get a Second Opinion with WellSpring",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and test results, choose a gastroenterologist from our listed experts, and get clear, independent advice on your condition and your treatment options.",
    "btn1": "Choose a gastroenterologist",
    "btn2": "How it works",
    "reviewText": "In-depth evaluations of Crohn's and ulcerative colitis plans, fatty liver and cirrhosis protocols, and complex digestive symptoms.",
    "steps": [
      {
        "h": "Pick a gastroenterologist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload endoscopy and colonoscopy reports, ultrasound or CT scans, liver function and blood tests, biopsy reports and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you are vomiting blood, passing black or bloody stools, have severe or worsening abdominal pain, yellowing of the eyes with confusion or drowsiness, or are unable to keep fluids down, go to the nearest emergency room immediately.",
    "types": null,
    "condTitle": "Conditions Gastroenterologists Look After",
    "conds": [
      "Acid reflux (GERD), heartburn and indigestion",
      "Stomach ulcers and H. pylori infection",
      "Irritable bowel syndrome (IBS) and chronic constipation or diarrhoea",
      "Inflammatory bowel disease (Crohn's disease and ulcerative colitis)",
      "Coeliac disease and food intolerances",
      "Fatty liver disease, hepatitis B and C, and cirrhosis",
      "Gallstones and pancreatitis",
      "Piles (haemorrhoids), fissures and other bowel problems",
      "Colon polyps and screening for bowel cancer"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been advised endoscopy, colonoscopy or surgery and want to know if it is needed",
      "You have a long-term condition such as IBD or liver disease and want to review your treatment",
      "You have been told to start long-term or strong medicines such as immunosuppressants or biologics",
      "Your symptoms continue despite treatment, or your test results seem unclear",
      "You have been told your tests are \"normal\" but your symptoms continue",
      "You want to understand your diagnosis, diet changes and alternatives"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "Endoscopy and colonoscopy reports (and images if available), ultrasound, CT or MRI reports, liver function tests and other blood tests, stool test results, biopsy or pathology reports, a list of current and past medicines, and any discharge summaries. A short diary of your symptoms, meals and bowel habits for one to two weeks can be very useful.",
    "docsEyebrow": "Gastroenterology",
    "docsTitle": "Our Gastroenterologists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-gastroenterology-1",
      "doctor-gastroenterology-2",
      "doctor-gastroenterology-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I had stomach problems for years and kept being told it was just acidity. The gastroenterologist went through my reports and history, explained what was actually happening in my gut, and told me which tests were worth doing and which were not.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“My liver reports showed abnormal results and I was very anxious. The doctor was calm, listened to everything, and explained my fatty liver in simple terms. I came away with a clear plan I could follow.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“Sharing my colonoscopy and blood reports was easy, the fee was clear upfront, and I had a video call within two days. The written summary helped me have a better conversation with my own doctor.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor perform my endoscopy or treat me?",
        "a": "No. The gastroenterologist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Should I stop my medicine before the consultation?",
        "a": "No. Continue your medicines as prescribed unless your doctor tells you otherwise."
      },
      {
        "q": "Can I share photos or reports from several different labs?",
        "a": "Yes. Share everything you have, including older reports. A complete history helps the specialist give a more accurate opinion."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get the opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": "Second opinions are for informational support and do not replace care from your treating doctor. Not for medical emergencies."
  },
  {
    "slug": "womens-health",
    "name": "Women's Health",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><circle cx=\"12\" cy=\"9\" r=\"4\"></circle><path d=\"M12 13v8M8 18h8\"></path></svg>",
    "eyebrow": "Women's Health",
    "h1": "Unsure About a Gynaecological or Reproductive Health Diagnosis, or a Recommended Procedure? Get a Second Opinion with WellSpring",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and scans, choose a women's health specialist from our listed experts, and get clear, independent advice on your condition and your treatment options.",
    "btn1": "Choose a women's health specialist",
    "btn2": "How it works",
    "reviewText": "Guidance on minimally invasive options for fibroids and endometriosis, pelvic reconstruction, and gynaecologic cancer care.",
    "steps": [
      {
        "h": "Pick a specialist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload ultrasound or MRI scans, Pap smears and biopsy reports, hormones and blood tests, pregnancy reports and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you are pregnant and have heavy bleeding, severe abdominal pain, reduced baby movements, leaking fluid, or a severe headache with blurred vision, or if you are not pregnant and have very heavy bleeding, fainting, or sudden severe pelvic pain, go to the nearest emergency room immediately.",
    "types": null,
    "condTitle": "Conditions Women's Health Specialists Look After",
    "conds": [
      "Irregular, heavy or painful periods",
      "Polycystic ovary syndrome (PCOS)",
      "Fibroids, ovarian cysts and endometriosis",
      "Pelvic pain and recurrent infections",
      "Pregnancy care and high-risk pregnancy",
      "Difficulty conceiving and fertility concerns",
      "Recurrent miscarriage",
      "Menopause symptoms and hormone-related concerns",
      "Abnormal Pap smear results and cervical health",
      "Urinary leakage and pelvic floor problems",
      "Cancers of the cervix, uterus and ovary (with oncology support)"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been advised a hysterectomy, fibroid removal or other gynaecological surgery",
      "You have been advised a caesarean section or told you have a high-risk pregnancy",
      "You have been advised fertility treatments such as IUI or IVF and want to understand your options",
      "Your periods or pelvic pain continue despite treatment",
      "Your ultrasound or Pap smear results are unclear",
      "You have been told to start long-term hormone treatment",
      "You have had repeated miscarriages with no clear explanation",
      "You want to understand your diagnosis and the alternatives, including non-surgical options"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "Ultrasound, MRI or CT scan reports (and images if available), Pap smear, HPV and biopsy reports, hormone and blood tests, pregnancy scans and antenatal reports, a list of current and past medicines, and any operative notes or discharge summaries. A short note about your menstrual cycle, symptoms, and past pregnancies or treatments is very helpful.",
    "docsEyebrow": "Women's Health",
    "docsTitle": "Our Women's Health Specialists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-womens-health-1",
      "doctor-womens-health-2",
      "doctor-womens-health-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I was told I needed a hysterectomy for fibroids and felt overwhelmed. The specialist went through my ultrasound, explained the size and position of the fibroids, and walked me through the options, including ones I had not been told about.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“I felt comfortable from the very first minute. The doctor listened to my entire history without hurrying, answered every question respectfully, and never made me feel judged.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“I shared my reports from home, the fee was clear from the start, and I received a clear written summary within two days. It helped my husband and me discuss the next steps with our own doctor with confidence.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor treat me or deliver my baby?",
        "a": "No. The specialist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Can I choose a female doctor?",
        "a": "Each profile on this page shows the specialist's name and photo, so you can choose a doctor you feel comfortable with."
      },
      {
        "q": "Is a video consultation suitable for gynaecological concerns?",
        "a": "In most cases, yes. Much of the opinion depends on your history, scans and test reports. If an in-person examination is needed, the specialist will tell you."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. Consultations are confidential, and you can share only what you are comfortable with. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  },
  {
    "slug": "urology",
    "name": "Urology",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M12 3c3 4 5 6.5 5 10a5 5 0 1 1-10 0c0-3.5 2-6 5-10z\"></path><path d=\"M10 15c.6 1 1.3 1.5 2.2 1.5\"></path></svg>",
    "eyebrow": "Urology",
    "h1": "Unsure About a Kidney, Bladder or Prostate Diagnosis, or a Recommended Procedure? Get a Second Opinion with WellSpring",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and scans, choose a urologist from our listed experts, and get clear, independent advice on your condition and your treatment options.",
    "btn1": "Choose a urologist",
    "btn2": "How it works",
    "reviewText": "Advanced second looks at prostate conditions, kidney and bladder concerns, urinary symptoms, and urologic procedures.",
    "steps": [
      {
        "h": "Pick a urologist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload ultrasound, CT or MRI scans, urine and blood tests, PSA results, biopsy reports and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you cannot pass urine at all, have severe pain in the side or lower abdomen with fever or vomiting, have heavy blood in your urine or clots, or have sudden severe testicular pain, go to the nearest emergency room immediately.",
    "types": null,
    "condTitle": "Conditions Urologists Look After",
    "conds": [
      "Kidney stones and ureteric stones",
      "Urinary tract infections that keep coming back",
      "Enlarged prostate (BPH) and difficulty passing urine",
      "Blood in the urine",
      "Urinary incontinence and overactive bladder",
      "Prostate, kidney and bladder cancers",
      "Raised PSA and prostate biopsy decisions",
      "Male infertility and erectile dysfunction",
      "Kidney blockage and narrowing of the urinary tract",
      "Conditions of the testicles, such as swelling or lumps",
      "Urological problems in children"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been advised surgery for kidney stones, an enlarged prostate or a blocked ureter",
      "You have been advised a prostate biopsy or told your PSA is high",
      "You have been advised to remove a kidney, the bladder or the prostate",
      "Your stones or infections keep coming back despite treatment",
      "You have been told to start long-term medicines for prostate or bladder problems",
      "Your symptoms continue despite treatment, or test results seem unclear",
      "You want to understand your diagnosis and the alternatives, including non-surgical options"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "Ultrasound, CT or MRI scan reports (and images if available), urine tests and cultures, kidney function and PSA blood tests, uroflowmetry or other bladder test reports, biopsy or pathology reports, a list of current and past medicines, and any operative notes or discharge summaries. A short diary of your symptoms, including how often you pass urine, any pain, and fluid intake over a few days, is very helpful.",
    "docsEyebrow": "Urology",
    "docsTitle": "Our Urologists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-urology-1",
      "doctor-urology-2",
      "doctor-urology-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I was told I needed surgery for a kidney stone and wasn't sure it was necessary. The urologist reviewed my scan, explained the size and position of the stone, and helped me understand when surgery is needed and when it can wait.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“I was uncomfortable discussing my symptoms, but the doctor was respectful and put me at ease from the first minute. He listened carefully and explained everything without rushing or making me feel embarrassed.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“Sharing my PSA and biopsy reports from home was easy, the fee was clear upfront, and I received a written summary within two days. It helped me ask better questions when I met my own doctor.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor perform my surgery?",
        "a": "No. The urologist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Is a video consultation suitable for urological problems?",
        "a": "In most cases, yes. Much of your opinion depends on your history, scans, and test reports. If an in-person examination is needed, the specialist will tell you."
      },
      {
        "q": "Can I speak to a specialist about sensitive concerns privately?",
        "a": "Yes. Consultations are confidential, and you can share only what you are comfortable with."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  },
  {
    "slug": "respiratory",
    "name": "Respiratory",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M4 13c2-5 5-7 8-7s6 2 8 7c-2 5-5 7-8 7s-6-2-8-7z\"></path><path d=\"M8 13h8M10 10l-2 3 2 3M14 10l2 3-2 3\"></path></svg>",
    "eyebrow": "Respiratory",
    "h1": "Unsure About a Lung or Breathing Diagnosis, or a Recommended Treatment? Get a Second Opinion with WellSpring",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and scans, choose a respiratory specialist from our listed experts, and get clear, independent advice on your condition and your treatment options.",
    "btn1": "Choose a respiratory specialist",
    "btn2": "How it works",
    "reviewText": "Diagnostic clarity for persistent pulmonary symptoms, complex lung conditions, respiratory therapies, and treatment pathways.",
    "steps": [
      {
        "h": "Pick a specialist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload chest X-rays, CT scans, lung function (spirometry) reports, sleep study results, blood tests, and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you have severe breathlessness, are unable to speak in full sentences, have blue or grey lips, are coughing up blood, or have chest pain with difficulty breathing, go to the nearest emergency room immediately.",
    "types": null,
    "condTitle": "Conditions Respiratory Specialists Look After",
    "conds": [
      "Asthma and allergic airway conditions",
      "Chronic obstructive pulmonary disease (COPD) and chronic bronchitis",
      "Chronic cough and breathlessness without a clear cause",
      "Pneumonia, tuberculosis and other lung infections",
      "Interstitial lung disease and pulmonary fibrosis",
      "Sleep apnoea and snoring",
      "Bronchiectasis",
      "Pleural conditions (fluid around the lungs)",
      "Lung nodules and post-COVID lung problems"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have a long-term cough or breathlessness, and the cause is not clear",
      "You have been diagnosed with asthma, COPD or lung fibrosis and want to review your treatment",
      "You have been advised long-term inhalers, oxygen, steroids or other strong medicines",
      "You have been advised bronchoscopy, a lung biopsy or surgery",
      "A scan shows a nodule or shadow, and you want to understand what it means",
      "You are not improving despite treatment",
      "You want to understand your diagnosis, outlook and alternatives"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "Chest X-ray and CT scan reports (and images if available), spirometry or other lung function reports, sleep study reports, blood and sputum test results, a list of current and past medicines and inhalers, and any hospital discharge summaries. A short note on when your symptoms occur, what triggers them, and any history of smoking, dust or chemical exposure is very helpful.",
    "docsEyebrow": "Respiratory",
    "docsTitle": "Our Respiratory Specialists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-respiratory-1",
      "doctor-respiratory-2",
      "doctor-respiratory-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I had a cough that would not go away for months, and different doctors gave me different explanations. The specialist went through my CT scan and breathing test with me, explained what was happening in my lungs, and told me what to follow up on.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“The doctor listened to my full history, including things I had not thought were important. He explained my asthma plan and showed me the right way to use my inhaler, which no one had done before.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“Sharing my scans and breathing test results from home was simple, the fee was clear from the start, and I received a written summary within two days. It made my next visit to my own doctor much more productive.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor treat me?",
        "a": "No. The respiratory specialist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Should I stop or change my inhalers before the consultation?",
        "a": "No. Continue your medicines as prescribed unless your doctor tells you otherwise."
      },
      {
        "q": "Do I need to share my actual scan images?",
        "a": "Reports are useful, but the images themselves (X-ray, CT) let the specialist give a more accurate opinion. Share both if you can."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": "Second opinions are for informational support and do not replace care from your treating doctor. Not for medical emergencies."
  },
  {
    "slug": "ophthalmology",
    "name": "Ophthalmology",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\"><path d=\"M3 12s3.5-5 9-5 9 5 9 5-3.5 5-9 5-9-5-9-5z\"></path><circle cx=\"12\" cy=\"12\" r=\"2.5\"></circle></svg>",
    "eyebrow": "Ophthalmology",
    "h1": "Unsure About Eye Diagnosis, or a Recommended Procedure? Get a Second Opinion with WellSpring",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Share your reports and scans, choose an ophthalmologist from our listed experts, and get clear, independent advice on your condition and your treatment options.",
    "btn1": "Choose an ophthalmologist",
    "btn2": "How it works",
    "reviewText": "Specialist second opinions on vision-threatening conditions, cataract and retinal care, glaucoma, and complex eye procedures.",
    "steps": [
      {
        "h": "Pick an ophthalmologist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload eye test reports, OCT and retinal scans, visual field tests, eye pressure readings, photographs of your eye and prescriptions."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor, and it is not for emergencies. If you have sudden loss or blurring of vision, a curtain or shadow over your sight, sudden flashes and many new floaters, severe eye pain with redness or nausea, or an eye injury or chemical splash, go to the nearest emergency room or eye hospital immediately.",
    "types": null,
    "condTitle": "Conditions Ophthalmologists Look After",
    "conds": [
      "Cataract",
      "Glaucoma (raised eye pressure and optic nerve damage)",
      "Diabetic eye disease (diabetic retinopathy)",
      "Age-related macular degeneration and other retinal conditions",
      "Retinal detachment and floaters",
      "Dry eye, eye allergies and infections",
      "Corneal conditions, including keratoconus",
      "Refractive errors (short sight, long sight, astigmatism) and glasses",
      "Squint (crossed eyes) and lazy eye in children",
      "Eye problems related to other conditions such as diabetes, thyroid disease or high blood pressure"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have been advised cataract surgery and want to understand the timing and the lens options",
      "You have been diagnosed with glaucoma or told your eye pressure is high and want to review treatment",
      "You have been advised laser treatment, injections in the eye, or retinal surgery",
      "You are considering LASIK or other vision correction surgery",
      "Your vision has changed and the cause is not clear",
      "Your symptoms continue despite treatment",
      "You want to understand your diagnosis, your outlook and the alternatives"
    ],
    "recordsTitle": "Records Worth Keeping Ready",
    "recordsText": "Previous eye examination reports, glasses or contact lens prescriptions, OCT, retinal photographs, visual field test and corneal topography reports (and images if available), eye pressure readings, a list of current eye drops and medicines, and any operative notes. If you have diabetes or high blood pressure, include your recent sugar and blood pressure readings. A short note on when your symptoms began, which eye is affected and what makes them better or worse is very helpful.",
    "docsEyebrow": "Ophthalmology",
    "docsTitle": "Our Ophthalmologists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-ophthalmology-1",
      "doctor-ophthalmology-2",
      "doctor-ophthalmology-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Real testimonials are published only with the patient's written consent, using the name, initials, or anonymity they choose.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I was told I needed cataract surgery and was confused about the different lens choices. The specialist reviewed my eye reports, explained how each option works, and helped me understand which one suited my daily life.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“My father's eye pressure was high and the glaucoma diagnosis frightened the whole family. The doctor took time to explain what it meant, why regular check-ups matter, and answered every question patiently.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“Sharing my OCT and eye test reports from home was simple, the fee was clear from the start, and I received a written summary within two days. It helped me discuss my treatment with my own doctor with confidence.”",
        "who": "— [Patient initials], [Age]",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor perform my eye surgery?",
        "a": "No. The ophthalmologist reviews your records and gives an independent opinion. You then decide whether to continue with your current doctor or consult someone else."
      },
      {
        "q": "Can an eye problem be assessed by video?",
        "a": "In many cases, yes. Much of the opinion depends on your history, test reports and scans. Some conditions need an in-person eye examination, and the specialist will tell you if that is the case."
      },
      {
        "q": "Should I stop my eye drops before the consultation?",
        "a": "No. Continue your eye drops and medicines as prescribed unless your treating doctor tells you otherwise."
      },
      {
        "q": "Is my medical data kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  },
  {
    "slug": "mental-health",
    "name": "Mental Health",
    "icon": "<svg viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"1.8\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M9 21v-3.2A7 7 0 1 1 18.6 9.5l1.4 3.3h-2v3.2a2 2 0 0 1-2 2h-2V21\"></path><path d=\"M11.2 12.6c-1-.9-1.7-1.4-1.7-2.3a1.2 1.2 0 0 1 2.2-.6 1.2 1.2 0 0 1 2.2.6c0 .9-.7 1.4-1.7 2.3l-.5.4z\"></path></svg>",
    "eyebrow": "Mental Health",
    "h1": "Unsure About a Mental Health Diagnosis or Treatment Plan? Get a Second Opinion",
    "lede": "We help patients get a second opinion from experienced specialists before making an important medical decision. Choose a mental health specialist from our listed experts, share your history and reports, and get clear, respectful, independent advice on your diagnosis, your medicines and your care options.",
    "btn1": "Choose a specialist",
    "btn2": "How it works",
    "reviewText": "Depression, anxiety, stress, sleep, and other emotional health concerns, with a focus on clear, respectful guidance.",
    "steps": [
      {
        "h": "Pick a specialist",
        "p": "Compare profiles, experience and fees."
      },
      {
        "h": "Share your records",
        "p": "Upload previous prescriptions, assessment or therapy reports, hospital summaries, and blood or brain scan reports if you have them. You can share only what you are comfortable with."
      },
      {
        "h": "Consult",
        "p": "Video call, phone call or a written review, as you prefer."
      },
      {
        "h": "Get a clear opinion",
        "p": "Receive a summary you can take to your treating doctor or therapist."
      }
    ],
    "alert": "A second opinion supports your decision-making. It does not replace your treating doctor or therapist, and it is not for emergencies. If you or someone you know is thinking about suicide or self-harm, or is in immediate danger, call your local emergency number (999 in Malaysia) or go to the nearest emergency room now. Please do not stop or change your medicines on your own. Speak to your doctor first.",
    "types": {
      "title": "Types of Mental Health Specialists",
      "items": [
        {
          "strong": "Psychiatrists",
          "text": "are medical doctors who diagnose mental health conditions, prescribe medicines and may also provide therapy"
        },
        {
          "strong": "Clinical psychologists",
          "text": "assess and treat conditions mainly through therapy and structured counselling (they do not prescribe medicines)"
        },
        {
          "strong": "Child and adolescent specialists",
          "text": "focus on emotional and behavioural concerns in young people"
        },
        {
          "strong": "De-addiction specialists",
          "text": "help with alcohol, tobacco, drug and other substance use"
        }
      ]
    },
    "condTitle": "Concerns Mental Health Specialists Look After",
    "conds": [
      "Depression and persistent low mood",
      "Anxiety, panic attacks and phobias",
      "Obsessive-compulsive disorder (OCD)",
      "Bipolar disorder and mood swings",
      "Schizophrenia and other psychotic conditions",
      "Post-traumatic stress and grief",
      "Sleep problems and insomnia",
      "ADHD and autism-related concerns in children and adults",
      "Stress, burnout and relationship difficulties",
      "Alcohol, tobacco and substance use",
      "Memory and thinking changes in older adults"
    ],
    "checkTitle": "When a Second Opinion Helps",
    "checks": [
      "You have received a diagnosis and want to confirm it or understand it better",
      "You are not improving despite treatment",
      "You have been advised a new, long-term or strong medicine and want to review it",
      "You are experiencing side effects from medicines",
      "You have been given different diagnoses by different professionals",
      "You are unsure whether you need medicines, therapy or both",
      "You are a parent seeking clarity on a child's behavioural or emotional concerns",
      "You want to understand your diagnosis, outlook and alternatives"
    ],
    "recordsTitle": "What to Keep Ready",
    "recordsText": "Previous prescriptions and a list of current and past medicines (with doses and how long you took them), earlier assessment or therapy reports, hospital discharge summaries, and any relevant blood tests or scans. A short note on when your symptoms began, how they affect sleep, work, study and relationships, and what has helped or not helped is very useful. Sharing is always your choice, and you can discuss anything further during the consultation.",
    "docsEyebrow": "Mental Health",
    "docsTitle": "Our Mental Health Specialists",
    "docsLede": "Each specialist is verified for qualifications and registration.",
    "doctors": [
      "doctor-mental-health-1",
      "doctor-mental-health-2",
      "doctor-mental-health-3"
    ],
    "storiesTitle": "Patient Stories",
    "storiesLede": "Patients share how the service helped them understand their condition, how their consultation went, and what the overall experience was like.",
    "storiesNote": "The stories below are illustrative samples. Because of the sensitive nature of mental health, real testimonials are published only with the patient's clear written consent, and always in the form they choose, including full anonymity.",
    "stories": [
      {
        "topic": "Understanding the diagnosis",
        "quote": "“I had been given a diagnosis that I did not fully understand and was worried about the medicines I was taking. The specialist went through my history and prescriptions, explained my condition in plain language, and told me what to discuss with my own doctor.”",
        "who": "— Anonymous, [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Experience with the specialist",
        "quote": "“I was nervous about talking to a stranger, but the doctor was kind, never rushed me and never judged me. I felt heard for the first time in a long while.”",
        "who": "— Anonymous, [Age]",
        "consent": "Published with consent"
      },
      {
        "topic": "Overall service",
        "quote": "“As a parent, I wanted clarity about my son's behaviour. We shared his reports from home, had a calm video call within a few days, and received a clear written summary that we could share with his school counsellor.”",
        "who": "— Anonymous, parent of a [Age]-year-old",
        "consent": "Published with consent"
      }
    ],
    "faqEyebrow": "FAQs",
    "faqTitle": "Common Questions",
    "faqs": [
      {
        "q": "Will the second-opinion doctor take over my treatment?",
        "a": "No. The specialist reviews your history and records and gives an independent opinion. You then decide whether to continue with your current doctor or therapist, or consult someone else."
      },
      {
        "q": "Should I stop or change my medicines before or after the consultation?",
        "a": "No. Do not stop or change psychiatric medicines on your own, as this can cause withdrawal symptoms or make symptoms return. Discuss any changes with your treating doctor."
      },
      {
        "q": "Is a video or phone consultation suitable for mental health concerns?",
        "a": "For many people, yes. Much of the opinion depends on your history, symptoms and previous records. If an in-person assessment is needed, the specialist will tell you."
      },
      {
        "q": "Do I have to share everything?",
        "a": "No. Share only what you are comfortable with. You can also share more during the consultation if you wish."
      },
      {
        "q": "Is my information kept private?",
        "pre": "Your records are shared only with the doctor you choose, for the purpose of your consultation. ",
        "link": "Read how we protect your information",
        "post": "."
      },
      {
        "q": "How soon will I get this opinion?",
        "a": "Timelines depend on the doctor and service type. The configured turnaround time for your selected service is shown before you confirm and pay."
      }
    ],
    "footnote": null
  }
];
